/**
 * Landing Page Content
 * Centralized content for easy updates and A/B testing
 *
 * Core positioning (v3, Jobs pass 2026-06-11): high-intent prospecting at the
 * PERSON level. Every competitor's hero says accounts and signals; this page
 * claims the person, and performs it instead of describing it. One primary
 * action: run your first search free. See the V3 region below for voice rules,
 * the standing hero rule, and gated-claim markers.
 */

// Single source of truth for the demo booking URL. Used by hero, final CTA, navbar.
export const DEMO_BOOKING_URL =
  'https://calendly.com/abubakarr_jaye-alumni/30min';

/**
 * Hero "Backed by" pill. Each entry maps to a downloaded logo at
 * /assets/backers/<id>.png (fetched via scripts/fetch-backer-logos.mjs).
 *
 * `enabled` gates the pill so it stays hidden until logos are downloaded.
 * Flip to true after running the fetch script.
 */
export const BACKERS = {
  enabled: true,
  label: 'Backed by',
  list: [
    { id: 'mit-e14-fund', name: 'MIT E14 Fund', url: 'https://www.e14.vc' },
    { id: 'sisu', name: 'Sisu', url: 'https://www.sisu.site' }
  ]
} as const;

// ============================================================================
// HERO SECTION
// ============================================================================

export const HERO_CONTENT = {
  headlineLine1: "Know who's in-market.",
  headlineLine2: 'Just describe your buyer.',

  subheadline:
    'Lumnis finds the people signaling intent in your market right now: engaging your competitors, hiring for what you sell, researching your category. Each one ranked by fit, with the reason they are worth a conversation.',

  // Self-serve first. Trial = primary, Demo = secondary.
  // Lower-friction primary lifts inbound conversion vs. sales-led demo-first.
  primaryCta: 'Start free trial',
  secondaryCta: 'Book a Demo',

  trustMicrocopy: ['Plugs into your CRM + stack', 'Set up in minutes'],

  // Legacy. Used by dead components (new-hero.tsx, etc.)
  rotatingExamples: [
    'VP Sales at fintech startups',
    'CTOs at Series A companies',
    'Founders who just raised'
  ],
  trialPlaceholder: '',
  trialButton: '',
  channels: '',
  alternativeHeadlines: {} as Record<
    string,
    { line1: string; line2: string; subheadline: string }
  >,
  buttonVariants: [] as string[]
} as const;

export const PERSONAS = {
  // Rendered as the "Built for" chip row above the logo marquee (Abu,
  // Jun 11). Kept out of the hero on purpose: corner-anchored chip rows
  // re-clutter the centered stack and push the marquee below the fold.
  list: [
    'Sales Development',
    'Account Executives',
    'Founders',
    'RevOps',
    'Demand Generation'
  ],
  label: 'Built for'
} as const;

/**
 * Hero announcement pill (above headline).
 *
 * Inspired by Juicebox's "LIVE | REGISTER FOR MAY 20 →". Free real estate for
 * proof, launches, webinars, momentum signals. Gate with `enabled` so it can
 * be toggled off without code changes when there's nothing live worth saying.
 *
 * `href` can be:
 *   - a same-page anchor (e.g. '#proof-bar'), handled as in-page scroll
 *   - an absolute URL (https://…), opens in a new tab
 * Set to null to render as a non-interactive pill.
 */
export const ANNOUNCEMENT = {
  enabled: false,
  label: 'NEW',
  text: '2.5× more replies than profile-only databases',
  href: '#proof-bar' as string | null
} as const;

// ============================================================================
// PROOF BAR (anonymized aggregate stats; slot between LogoMarquee + HowItWorks)
// ============================================================================
//
// Source: scripts/search-type-reply-rate-analysis.ts
// Snapshot: 2026-05-24. 539 AI campaigns across customer organizations.
// 20,516 enrolled · 3,201 DMs sent · 674 replies.
// Reply rate by search type: intent-based 38% vs profile-only 16%
// (the underlying 2.5× claim; the actual computed ratio is 2.36× but we
// keep the round-number framing — within reasonable rounding tolerance,
// and the bar is the conservative version of the claim, not the loud one).
//
// Re-run the script and refresh these numbers monthly. Keep them rounded
// down so we never overstate; copy is intentionally vague on denominator.

export const PROOF_BAR = {
  eyebrow: 'The signal advantage',
  // Headline split for two-tone rendering: lead in ink, rest muted gray.
  headlineLead: '2.5× more replies',
  headlineRest: 'than profile-only static database searches.',
  // Plain version kept for any consumer that wants the unbroken string.
  headline: '2.5× more replies than profile-only static database searches.',
  // 3-stage funnel sourced from scripts/search-type-reply-rate-analysis.ts.
  // Snapshot 2026-05-24: 539 AI campaigns.
  //   Enrolled: 20,516  ·  DMs sent: 3,201  ·  Replies (Lumnis): 674
  // Numbers rounded down so we never overstate. Re-run the script monthly.
  stages: [
    {
      name: 'Prospects sourced',
      value: '20,500+',
      barPercent: 100
    },
    {
      name: 'Messages sent',
      value: '3,200+',
      barPercent: 70
    },
    {
      name: 'Replies',
      value: '670+',
      barPercent: 28,
      highlight: true,
      callout: '2.5× lift vs profile-only search'
    }
  ],
  // Footnote now includes the underlying reply-rate comparison (38% vs 16%)
  // so curious readers can sanity-check the "2.5×" headline without needing
  // it spelled out in the main proof bar.
  footnote:
    'Intent-based search hits 38% reply rate vs 16% on profile-only search across our customer base. Aggregate across all customer AI campaigns; reply rate measured on direct messages sent post-connection.'
} as const;

// ============================================================================
// HOW IT WORKS SECTION
// ============================================================================

/**
 * How It Works tabbed section.
 *
 * Each step now follows the Juicebox copy pattern: small uppercase eyebrow,
 * outcome-driven H2 with a single marker-highlighted phrase, descriptive
 * paragraph, and a per-tab feature CTA alongside the universal Book a Demo.
 *
 * `cta.target` is resolved in the component:
 *   - 'signup' → routes.dashboard.auth.SignUp
 *   - 'demo'   → DEMO_BOOKING_URL (opens new tab)
 * Add a 'scroll' variant if a future tab should anchor to a section.
 */
export const HOW_IT_WORKS_STEPS = {
  sectionTitle: 'How it works',
  // Universal secondary CTA rendered on every tab.
  demoCtaLabel: 'Book a Demo',
  steps: [
    {
      number: '1',
      eyebrow: 'SEARCH',
      // Headline is split: <prefix><highlight><suffix>. The highlight is
      // rendered with the same mint marker style used in the hero prompt.
      headlinePrefix: 'Find buyers across ',
      headlineHighlight: '800M+ profiles',
      headlineSuffix: ' in plain English',
      description:
        "Describe your ICP the way you'd describe it to a teammate. Lumnis turns it into an intent-based search across 800M+ profiles, surfacing people with real buying signals and ranking them by fit, not a static database dump.",
      cta: { label: 'Try Lumnis Search', target: 'signup' as const }
    },
    {
      number: '2',
      eyebrow: 'WHY NOW',
      headlinePrefix: 'Every account comes with ',
      headlineHighlight: 'the signal behind it',
      headlineSuffix: '',
      description:
        "Competitor intent, problem intent, category intent. Lumnis tells you why each person is in-market right now: engaging a competitor's post, hiring for the problem you solve, or researching your space. That reason is the difference between a warm opening and a cold list.",
      cta: { label: 'See the signals', target: 'signup' as const }
    },
    {
      number: '3',
      eyebrow: 'ACT',
      headlinePrefix: 'Push it to ',
      headlineHighlight: 'your stack',
      headlineSuffix: ', or reach out through ours',
      description:
        'Send the ranked, signal-annotated list straight into Salesforce, HubSpot, Outreach, or Salesloft. Prefer to run outreach in Lumnis? It’s an optional add-on, and your reps approve every first touch.',
      cta: { label: 'Start free trial', target: 'signup' as const }
    }
  ]
} as const;

// ============================================================================
// THE ENGINE (BENTO GRID)
// ============================================================================

/**
 * Engine section, focal-panel layout.
 *
 * Structure rendered by engine-section.tsx:
 *   1. Eyebrow + H2 + subtitle
 *   2. One mint-textured focal panel containing:
 *        - Floating agent annotation chips (positioned by viewport)
 *        - Central live-pipeline activity stack
 *   3. Closer pill (the learning loop)
 *
 * Status lines use middle-dot (·) as the soft separator between subject and
 * detail, matching the activity-feed convention.
 */
export const ENGINE_CONTENT = {
  eyebrow: 'What Lumnis watches',
  sectionTitle: 'Always watching. Always learning.',
  subtitle:
    'Lumnis monitors your market, detects buying signals, and ranks accounts by fit, learning from every outcome so the list gets sharper each week.',

  // Floating annotation chips around the focal panel. Each chip carries:
  //  - icon: lucide name (mapped in engine-section.tsx)
  //  - tone: visual color family for the icon tile (blue/amber/mint/violet)
  //  - start: pixel offset + rotation the chip flies in FROM on first reveal
  //
  // Note: the Learning Agent was removed from the chip set on purpose. Learning
  // is the moat, not a sibling agent — it's represented by the closer pill
  // below the panel ("LEARNS WHAT WORKS"). Inbox Infra Agent took the 4th slot
  // because it surfaces our in-house domain warming, which is a real
  // differentiator that buyers otherwise don't see until the integrations grid.
  agents: [
    {
      id: 'research',
      name: 'Research Agent',
      status: 'Reading 12 signals on Maya Chen',
      icon: 'search',
      tone: 'blue' as const,
      start: { x: -90, y: -50, rot: -3 }
    },
    {
      id: 'outreach',
      name: 'Outreach Agent',
      status: 'Drafting intro · 38 words',
      icon: 'edit',
      tone: 'slate' as const,
      start: { x: 90, y: -50, rot: 3 }
    },
    {
      id: 'reply',
      name: 'Replies Agent',
      status: 'Routing 3 warm leads to calendar',
      icon: 'reply',
      tone: 'mint' as const,
      start: { x: -80, y: 60, rot: -2 }
    },
    {
      id: 'infra',
      name: 'Inbox Infra Agent',
      status: 'Warming hello.lumnis.so · 96%',
      icon: 'shield',
      tone: 'violet' as const,
      start: { x: 90, y: 60, rot: 2 }
    }
  ],

  // Three-column feature pillars rendered between the focal panel and the
  // closer pill. Tags color-coded to match the agent palette: research/reply
  // both mint (the "what we deliver" outcomes), outreach amber (the
  // crafting/sending step).
  pillars: [
    {
      id: 'research',
      tag: 'Signal',
      tone: 'mint' as const,
      title: 'Real intent signals, not scraped lists',
      body: "Profiles, recent posts, job changes, funding, hiring activity, all synthesized into who's ready to buy right now."
    },
    {
      id: 'reply',
      tag: 'Why now',
      tone: 'mint' as const,
      title: 'Every account comes with its reason',
      body: 'Competitor engagement, hiring triggers, funding, category research: the signal that flagged each account, attached so your team knows why now.'
    },
    {
      id: 'outreach',
      tag: 'Outreach · optional',
      tone: 'slate' as const,
      title: 'Reach out, or hand off to your stack',
      body: 'Push ranked lists into Salesforce, HubSpot, Outreach, or Salesloft, or run outreach in Lumnis. Your reps approve every first touch.'
    }
  ],

  // Sample human-in-the-loop card. Shows a drafted LinkedIn message ready
  // for approval — demonstrates what the Outreach Agent actually produces.
  // (Previously showed an evaluation prompt + prospect bio; switched to a
  // drafted message because that's the more concrete "agents draft, you
  // approve" story for Lumnis.)
  interaction: {
    eyebrow: 'Drafted intro',
    channel: 'LinkedIn',
    recipient: 'Maya Chen · Ledgerly',
    body: "Hi Maya, congrats on the Series A. Saw you're hiring your first GTM rep at Ledgerly. We help Series A founders book 15+ qualified calls/month without an SDR. Worth a quick chat next week?",
    approveLabel: 'Approve & send',
    rejectLabel: 'Reject'
  },

  replayLabel: 'Replay',

  closer: {
    label: 'LEARNS WHAT WORKS',
    text: 'Every signal and outcome trains the ranking. Intent-based search already lands 2.5× more replies than profile-only lists, and rising.'
  }
} as const;

// ============================================================================
// INTEGRATIONS SECTION
// ============================================================================
//
// Juicebox-style grid: real logos + in-house chips + center callout + hatched
// empty cells. Layout assembled in components/landing/integrations-section.tsx.

export const INTEGRATIONS_CONTENT = {
  eyebrow: 'Integrations',
  sectionTitleLead: 'Plugs into',
  sectionTitleRest: 'your stack',
  subtitle:
    'Your open deals come straight from Salesforce, HubSpot or Attio, and what Lumnis finds goes back into the tools your team already runs. The data infrastructure is built in, so you don’t stitch it together.',
  // 'kind' drives the cell renderer:
  //   - 'logo'    → IntegrationLogos[name] component
  //   - 'inhouse' → text chip with Lumnis brand color (no third-party brand)
  cells: [
    { id: 'hubspot', label: 'HubSpot', kind: 'logo' as const },
    { id: 'attio', label: 'Attio', kind: 'logo' as const },
    { id: 'gmail', label: 'Gmail', kind: 'logo' as const },
    { id: 'outlook', label: 'Outlook', kind: 'logo' as const },
    { id: 'linkedin', label: 'LinkedIn', kind: 'logo' as const },
    { id: 'calendly', label: 'Calendly', kind: 'logo' as const },
    { id: 'gcal', label: 'Google Calendar', kind: 'logo' as const },
    { id: 'mcp', label: 'MCP', kind: 'logo' as const },
    { id: 'salesforce', label: 'Salesforce', kind: 'logo' as const },
    { id: 'domain', label: 'Domain provisioning', kind: 'inhouse' as const },
    { id: 'monitor', label: 'Delivery monitoring', kind: 'inhouse' as const },
    {
      id: 'enrich',
      label: 'Email + phone enrichment',
      kind: 'inhouse' as const
    }
  ]
} as const;

// ============================================================================
// TESTIMONIALS
// ============================================================================

export const TESTIMONIALS = {
  sectionTitle: 'What our customers are saying',
  // Featured pull quote. quoteLead = ink-colored emphasis; quoteRest = soft
  // gray secondary clause. Both render in the same blockquote.
  // Based on the Aug 24 account-intelligence call, with wording edited for
  // clarity by Abu. Not a verbatim transcript quote. This is qualitative
  // feedback, not a measured claim about reply rates or productivity.
  // Source: hq/transcripts/Abu/2026/08-Aug/week-4/2026-08-24-account-
  // intelligence-feature-research-prospecting-and-linkedin-outreac.md
  // Role and company stage are stated in the Aug 18 call with the same AE.
  // GATED: Anonymous preview attribution requested by Abu; quote publication
  // permission is still pending. Do not add the person's name or company.
  featured: {
    quoteLead: 'The trustworthiness of the research',
    quoteRest: 'enables us to do serious personalization.',
    name: 'Account executive',
    title: '',
    company: 'Seed-stage AI company'
  },
  // Legacy 3-quote array kept for any consumers that still reference it.
  quotes: [
    {
      quote:
        "Probably the best AI writing tool I've seen. The way it writes is very natural. People are responding as if it's a person.",
      name: 'Ben',
      title: 'Co-founder',
      company: 'Nightingale'
    },
    {
      quote:
        'Getting a prospecting list of that quality in 20 minutes is crazy.',
      name: 'Gabby',
      title: 'GTM Lead',
      company: ''
    },
    {
      quote: 'Night and day better than any other prospecting tool.',
      name: 'AfterQuery',
      title: '',
      company: 'YC W23'
    }
  ]
} as const;

// ============================================================================
// PRICING TIERS
// ============================================================================

/**
 * §6 Pricing. Printed $5,000 (Essentials, flat per team of up to 5)
 * qualifies the visitor and signals self-serve confidence. 6+ seat teams
 * live in the Team tier conversation (anchor per-seller, never printed).
 * No credits math, no sending line items.
 * (Essentials is the billing 'scale' product; see PRICING_TIERS.)
 */
export const PRICING_TIERS = {
  sectionTitle: 'Start free. Stay because the list is right.',
  subtitle: '',
  timeFrame: '',
  seePricing: { label: 'See pricing', href: '/pricing' },
  tiers: [
    // Prices + credit numbers mirror @workspace/billing config (Stripe truth:
    // the entry paid tier is the billing 'scale' product, relabeled
    // "Essentials" — FLAT $5,000/team/mo, 5,000 credits/mo, up to 5 seats;
    // free = 500 credits one-time). The old $749 Launch and $2,499/seat
    // prices are retired from display. Never print a price the checkout
    // won't charge.
    {
      name: 'Free',
      price: '$0',
      period: '',
      description:
        "Your first search. See your market's shortlist before you pay anything.",
      features: [
        '500 prospect credits (one-time)',
        'One full search of your market',
        'The receipt on every name',
        'Verified email + direct phone'
      ],
      cta: 'Run your first search free',
      highlighted: false
    },
    {
      name: 'Essentials',
      price: '$5,000',
      period: '/month',
      description:
        'One flat price for the whole team, up to five seats. No per-seat math.',
      features: [
        '5,000 prospect credits a month',
        'Searches that run every week',
        'The receipt on every row',
        'Verified email + direct phone'
      ],
      cta: 'Get started',
      highlighted: true
    },
    {
      name: 'Team',
      price: "Let's talk",
      period: '',
      description: 'Priced per seller, for teams of six AEs and up.',
      features: [
        'Credits scale with every seller',
        'Shared market coverage across the team',
        'A direct line to us'
      ],
      cta: 'Talk to us',
      highlighted: false
    }
  ]
} as const;

// ============================================================================
// FINAL CTA
// ============================================================================

/** Close on continuous prioritization, using the same signup action as the hero. */
export const FINAL_CTA_CONTENT = {
  // Split for two-tone rendering: lead recedes, destination clause lands.
  headlineLead: 'Stop guessing',
  headlineRest: 'where to focus.',
  headline: 'Stop guessing where to focus.',
  description: 'See what Lumnis would prioritize for your team this week.',
  buttonText: 'Get started free',
  trustLine: 'No credit card required. Set up in 10 minutes.'
} as const;

// ============================================================================
// V4 — "inspiration pass" landing page (2026-06-11, supersedes v3 in place).
// One primary action: run your first search free, repeated after §4 and §6.
// v4 deltas: hero results render INSTANTLY (no replay choreography), §3
// compressed to caption weight (spray-list visual cut), §4 numbered beats +
// timeline rail + repeat CTA, §5 stats band (new), §6 testimonial wall
// (count-gated), under-CTA lines now latency-proof ("One sentence is the
// whole setup" replaces "First list in minutes").
//
// VOICE RULES for everything in this region (rendered copy only):
//   - No em dashes. Use a period, comma, or colon instead.
//   - Never when describing Lumnis: outbound, AI SDR, meetings
//     booked, autopilot, "AI agents", "signal-based selling", 24/7,
//     "stop guessing", unleash, supercharge, seamless, effortless,
//     revolutionize, game-changing, exclamation points.
//     (Words inside customer quotes and FAQ category labels are exempt.)
//   - "High-intent" always attaches to people / person-level, never accounts.
//   - "Outreach" is allowed only as the name of a play (top-account outreach,
//     conference outreach), never as what Lumnis is (deck v5, Oct 10).
//
// Hero examples illustrate the full GTM intelligence offering under the
// approved assumption that these capabilities are implemented. Other sections
// retain their existing `// GATED:` markers for launch review.
// ============================================================================

/**
 * §1 Hero: the deck v5 one-liner (Oct 10), "Lumnis does the research and
 * relationship-building for B2B sales teams." The centered signup CTA and
 * backer treatment stay unchanged.
 */
const HERO_EYEBROW_PHRASES = [
  'Built by AI researchers',
  'from Microsoft, Amazon and JPMorgan'
] as const;

const HERO_HEADLINE_LINES = [
  'Research and relationship-building',
  'for B2B sales teams'
] as const;

/**
 * Warm pipeline positioning (Abu, Nov 1): the homepage hero sub and every
 * link preview (title, description, OG image) read from here.
 */
export const WARM_PIPELINE_COPY = {
  title: 'Warm pipeline for sales teams',
  subheadline:
    'See which accounts are in market this week and who can get you in the door (your first meeting within days).'
} as const;

export const HERO_V3 = {
  // Keep the card implementation available while comparing a quieter hero.
  showIllustrations: false,
  eyebrow: HERO_EYEBROW_PHRASES.join(' '),
  eyebrowPhrases: HERO_EYEBROW_PHRASES,
  headline: HERO_HEADLINE_LINES.join(' '),
  headlineLines: HERO_HEADLINE_LINES,
  subheadline:
    'Lumnis runs the sales plays your team can’t scale. It finds the accounts worth working this week, researches the buyers and prepares the next move.',
  cta: 'Get started free',
  underCta: 'No credit card required. Set up in 10 minutes.'
} as const;

/** Illustrative hero receipts, not live account or audience data. */
export const HERO_RECEIPTS = [
  {
    category: 'Account change',
    subject: 'Quillstack',
    observation: 'New commercial leader',
    nextMove: 'Refresh the buying group.',
    evidence: 'Company announcement'
  },
  {
    category: 'Market insight',
    subject: 'Category conversations',
    observation: 'Migration questions gaining attention',
    nextMove: 'Address migration concerns.',
    evidence: 'Public conversations'
  },
  {
    category: 'Account priority',
    subject: 'Brightvault',
    observation: 'New signals on an existing opportunity',
    nextMove: 'Review with the AE.',
    evidence: 'Public activity + CRM context'
  }
] as const;

/**
 * §3 The problem. One section, one realization, one scroll-stop.
 * H2 alone is signable (OpenFunnel says it near-verbatim), so it must never
 * travel without ¶1's twist: the targeted list is how spray and pray lives on.
 *
 * PLACEHOLDER NOTE: vignette artifact cards must become anonymized REAL
 * screenshots, date-stamped, before publish (checklist). Until then they are
 * illustrative recreations.
 */
export const PROBLEM_CONTENT = {
  eyebrow: 'Why continuous matters',
  headline: 'Your go-to-market team is flying blind.',
  // The diagram names the stages; its introduction explains the opportunity.
  diagramIntro: 'Buyers move through four stages.',
  diagramOpportunity: 'The window to act is in the middle two.',
  signalStackingLead:
    'The right timing comes from connecting signals, not chasing one.',
  turn: "A buyer is in the window about 3% of the year. The game is knowing who's in it this week.",
  // THE CENTERPIECE: the awareness-stage circle. Plain words, not marketer
  // jargon (Schwartz vocabulary lives in the manifesto/docs version).
  // We do NOT circle "Already chosen": honesty is the differentiator.
  journey: {
    stages: [
      'Not looking',
      'Feeling the pain',
      'Comparing options',
      'Already chosen'
    ],
    circledStages: [1, 2],
    circleLabel: 'This is the window to act.',
    // Signals slot under the stage they reveal.
    stageSignals: {
      1: ['Pain posts', 'Topic engagement', 'Relevant hiring'],
      2: [
        'Competitor engagement',
        'Review activity' // GATED: reviews. Cut this string if the reviews signal slips the launch gate.
      ]
    } as Record<number, readonly string[]>,
    // The 3% line moved up into the prose `turn`; the caption states what
    // Lumnis does with the circle.
    caption: "Lumnis finds who's inside this circle, every week."
  },
  // The three vignette buyers standing inside the circle. Each card: the
  // artifact (what they did in public), a meta stamp, and ONE plain line on
  // why you'd reach out now (Abu, Jun 11: the old two-line read was
  // confusing). Personas fictional, faces are stock avatars. Vignette 2 is
  // engagement detection (LIVE), not the funding signal: do not cut it if
  // funding slips the gate.
  vignettes: [
    {
      id: 'cfo',
      kind: 'quote',
      who: 'A CFO',
      img: 'https://i.pravatar.cc/120?img=60',
      artifact: 'Our legacy ERP is where projects go to die.',
      meta: 'Commented · 3 weeks ago',
      why: 'Feeling the pain out loud. Reach out before the eval starts.'
    },
    {
      id: 'founder',
      kind: 'quote',
      who: 'A founder',
      img: 'https://i.pravatar.cc/120?img=31',
      artifact: 'Pitch us in the comments. We are deploying this quarter.',
      meta: "Replied under a VC's post · this week",
      why: 'Buying this quarter and says so. Get there before the shortlist forms.'
    },
    {
      id: 'security-team',
      kind: 'example',
      who: 'A security team',
      company: 'Brightvault',
      companyInitial: 'B',
      artifact:
        'Hiring platform security engineers while team members engage with two competing vendors.',
      meta: 'Hiring + competitor engagement',
      why: 'Together, these signals point to an active evaluation window.'
    }
  ]
} as const;

/**
 * §4 The product, one telling. Three beats + close.
 * Beat 2 is the SINGLE body location for all gated signals (claim map).
 * Beat 1 is performed, not claimed: the typed-sentence demo.
 */
export const PRODUCT_CONTENT = {
  eyebrow: 'The product',
  headline: 'One engine. Three ways to use it.',
  // The "Then it's yours" close + §4 repeat CTA were CUT (Abu, Jun 11):
  // the learning-loop moat now lives in the hero eyebrow tagline + footer,
  // and §6/pricing/final CTA carry the remaining CTA cadence.
  beats: [
    // Tightened (Abu, Jun 11): roughly half the words per beat. Beat 02
    // remains the gated-claims home: role moves + stated pain + funding stay
    // in copy; tools/reviews now live only in the stream visual.
    {
      id: 'describe',
      title: 'Describe what matters.',
      timeline: 'minute one',
      body: "People, companies, or an audience. Use plain English, the way you'd brief a colleague.",
      caption: ''
    },
    {
      id: 'reads',
      title: 'It reads your whole market.',
      timeline: 'this week',
      body: "Public behavior, people movement, hiring, company changes, market conversation, CRM context, relationships, and who your team is already working. It works even if nobody's heard of you yet.",
      caption: 'It reads.'
    },
    {
      id: 'shortlist',
      title: 'You get the judgment.',
      timeline: 'every week, sharper',
      body: 'A ranked opportunity, an account brief, or a view of what your audience cares about—each with the evidence behind it.',
      caption: 'One view of the account, shaped for the job.'
    }
  ],
  typedSentence:
    'Show me the people and companies that matter in cloud security—and what the market cares about now.',
  // Beat 2 stream: raw market activity with the people attached (faces are
  // stock avatars, all personas + companies fictional). Trimmed to 5 rows
  // (Abu, Jun 11): reviews + stated-pain rows cut; stated pain stays in the
  // Beat 02 body copy (gated-claims home), reviews currently renders nowhere.
  // Priya threads Beat 02 → Beat 03 on purpose: she surfaces in the reading,
  // then tops the shortlist.
  streamItems: [
    {
      who: 'Priya Nair',
      img: 'https://i.pravatar.cc/120?img=45',
      text: 'engaged with 2 competing cloud-security vendors',
      kind: 'competitor' as const,
      when: '3d'
    },
    {
      who: 'Brightvault',
      img: null,
      text: 'opened 2 security engineer roles',
      kind: 'hiring' as const,
      when: '5d'
    },
    {
      who: 'Dana Kim',
      img: 'https://i.pravatar.cc/120?img=16',
      text: 'started as Head of Security',
      kind: 'role' as const,
      when: '1mo'
    }, // GATED: role moves
    {
      who: 'Tributary',
      img: null,
      text: 'has an active opportunity in evaluation',
      kind: 'crm' as const,
      when: 'now'
    },
    {
      who: 'Quillstack',
      img: null,
      text: 'was researched by 2 sales reps this week',
      kind: 'sales' as const,
      when: '4d'
    }
  ]
} as const;

/**
 * §5 Stats band (new in v4, Gojiberry pattern): three numbers in a strip,
 * each with a defining sublabel (define-it-in-the-same-breath, typeset).
 * Renders directly ABOVE the proof screen, nothing between them: the band
 * asserts, the screen proves one swipe later.
 *
 * Substantiation gate before publish: dataset + as-of date behind 2.5× and
 * ~40%; once substantiated, prefer the EXACT figure (Halbert: "77.6%" beats
 * "most"). "1 sentence" replaces a minutes claim deliberately: latency-proof,
 * and it sells the no-ops setup.
 */
export const STATS_BAND = {
  stats: [
    {
      value: '2.5×',
      sublabel:
        'more replies than static-list prospecting, across our customers'
    },
    {
      value: '~40%',
      sublabel: 'of first messages answered, for our best customers'
    },
    // The product truth no competitor in the lane can sign (and the answer
    // to PhantomBuster's bare score chip). Substantiable today: per-signal
    // evidence rendering is live. Replaced "800M+" (volume claim on a
    // depth-thesis page, and it was Juicebox's number).
    {
      value: '100%',
      sublabel:
        'of names come with the receipt: the post, the comment, the thing that changed'
    }
  ]
} as const;

/**
 * §6 Proof: the screen + the wall. The stat pair moved UP to the stats band;
 * this section keeps the evidence (screen) and the voices (wall).
 *
 * ⚠️ PRE-PUBLISH: the table below is a mock and the admission line renders
 * as-is. Capture + annotate the real customer screen before launch so the
 * line is true.
 *
 * Wall quotes render as-is (Abu: no gating). Permission notes stay as
 * comments; remove a quote on request, not via flags.
 */
export const PROOF_V3 = {
  eyebrow: 'Proof',
  headline: 'The proof is in the replies.',
  wall: [
    {
      // Draft from a verified event; swap for Doug's exact wording
      // post-close (Fri Jun 12).
      text: 'I booked a meeting off the first shortlist, before the rest of the team was even connected.',
      name: 'Doug R.',
      category: 'Native Security (5-AE cloud-security team)'
    },
    {
      // Verbatim Apr 10. Advisor disclosure rides the category tag.
      text: 'My fear is that this works too well.',
      name: 'Adam',
      category: 'Lifted.vc (B2B coaching) · advisor'
    },
    {
      text: '20%+ replies in the first three days.',
      name: 'David',
      category: 'Nightingale (healthcare AI)'
    }
    // Slot 4: AfterQuery once its three permissions land (wording · logo ·
    // naming the labs; fallback "two frontier AI labs"), or Louis/ViziApps
    // post-trial.
  ],
  screen: {
    // ⚠️ Pre-publish: this chart is a mock built from real aggregate data.
    // Swap in the real annotated customer capture before launch.
    title: 'Replies by strategy',
    rows: [
      {
        strategy: 'Commented on pain posts',
        sent: 41,
        replies: 16,
        rate: '39%',
        highlight: true
      },
      {
        strategy: 'Watching rival vendors',
        sent: 36,
        replies: 11,
        rate: '31%',
        highlight: false
      },
      {
        strategy: 'Hiring for the problem',
        sent: 52,
        replies: 13,
        rate: '25%',
        highlight: false
      },
      {
        strategy: 'Static list (control)',
        sent: 120,
        replies: 19,
        rate: '16%',
        highlight: false
      }
    ],
    annotation: 'The best strategies hear back on about 4 in 10 first messages.'
  },
  repeatCta: 'Run your first search free'
} as const;

// ============================================================================
// LEGACY EXPORTS. Dead components still import these. Keep until archived.
// ============================================================================

export const COST_CALCULATOR = {
  sectionTitle: '',
  subtitle: '',
  defaults: {
    listBuildingHours: 0,
    clayEnrichmentHours: 0,
    templateWritingHours: 0,
    personalizingHours: 0,
    followUpHours: 0,
    hourlyRate: 0
  },
  fields: [] as { id: string; label: string; unit: string }[],
  results: {
    timeLabel: '',
    timeSuffix: '',
    costLabel: '',
    costSuffix: '',
    withLumnisLabel: '',
    withLumnisValue: '',
    savingsLabel: '',
    questionLine: ''
  },
  toolCosts: { salesNav: 0, clay: 0, heyReach: 0, total: 0 },
  cta: '',
  ctaVariants: [] as string[]
} as const;
export const COST_COMPARISON = {
  sectionTitle: '',
  columns: [] as string[],
  rows: [] as { label: string; values: string[]; highlight?: boolean }[],
  conclusion: ''
} as const;
export const COMPETITOR_COMPARISON = {
  sectionTitle: '',
  subtitle: '',
  rows: [] as {
    feature: string;
    others: string;
    lumnis: string;
    lumnisHighlight: boolean;
  }[]
} as const;
export const CONTROL_CONTENT = {
  sectionTitle: '',
  subtitle: '',
  description: '',
  features: [] as { icon: string; title: string; description: string }[],
  previewMessage: '',
  previewActions: [] as string[]
} as const;
/**
 * FAQ: four questions (Abu, Jun 11: cut the weak-signal and quiet-niche
 * items; earlier passes cut "What counts as in-market?" → docs and "How do
 * I know the data's right?" → dead). "AI SDR" as a category label would be
 * allowed here per the voice-guide scope, but none of the four need it.
 */
export const LANDING_FAQ = {
  sectionTitle: 'Questions, answered',
  items: [
    {
      question: 'How is this different from ZoomInfo or Apollo?',
      answer:
        'They sell contacts. Lumnis tells you which people are in-market and why, with the evidence. A list is the start of the guessing; a shortlist with receipts is the end of it.'
    },
    {
      question: "Can't I just use ChatGPT?",
      answer:
        "For one person, sure. Give it twenty minutes and ChatGPT will write you a decent profile. The shortlist is a different job. Lumnis runs purpose-built search across your whole market on licensed engagement data ChatGPT can't see: who commented on what, who keeps circling your competitors, who just started hiring. It scores every person on intent and fit, attaches the evidence, finds the verified email and direct phone, remembers everyone you've already found or talked to, and runs again every week. ChatGPT can describe your buyer. It can't watch your market."
    },
    {
      question: 'Do you contact people for me?',
      answer:
        'You can take the list into whatever you use, and Lumnis can help you act on it if you want. But volume was never the bottleneck; knowing is. The product is the knowing; your motion stays yours.'
    },
    {
      question: 'Where does this data come from?',
      answer:
        'Publicly visible professional activity: posts, comments, engagement, job listings, role changes, gathered through licensed data providers, plus business contact data from enrichment vendors. Nothing private. Every claim links to its source, so the receipt that proves intent is the same one that shows where it came from.'
    }
  ]
} as const;
export const BENEFITS_CONTENT = {
  sectionTitle: '',
  benefits: [] as { icon: string; text: string }[],
  microLine: ''
} as const;
export const SHIFT_CONTENT = {
  sectionTitle: '',
  comparison: {
    before: {
      label: '',
      tasks: [] as { task: string; hours: string }[],
      result: '',
      feeling: ''
    },
    after: { label: '', input: '', process: '', result: '', feeling: '' }
  }
} as const;
export const COMPOUND_CONTENT = {
  sectionTitle: '',
  weeks: [] as { week: string; headline: string; description: string }[]
} as const;
export const WHO_ITS_FOR_CONTENT = {
  sectionTitle: '',
  personas: [] as string[],
  integrationLine: ''
} as const;
export const KEY_FEATURES = {
  sectionTitle: '',
  subtitle: '',
  features: [] as {
    icon: string;
    title: string;
    description: string;
    metric: string;
  }[]
} as const;
export const PROOF_CONTENT = {
  headline: '',
  benefits: [] as { headline: string; description: string }[],
  metrics: [] as { value: string; label: string }[]
} as const;
export const INTEGRATIONS = {
  line: '',
  icons: [] as { name: string; url: string }[]
} as const;
export const HOW_IT_WORKS_CONTENT = {
  sectionTitle: '',
  steps: [] as { title: string; description: string; icon: string }[]
} as const;
export const HERO_DEMO = {
  panels: {
    learned: {
      title: '',
      profile: { name: '', title: '', details: [] as string[] }
    },
    wrote: { title: '', message: '' },
    happened: { title: '', results: [] as { icon: string; text: string }[] }
  }
} as const;
export const TRIAL_PREVIEW = {
  loadingStates: [] as string[],
  resultHeadline: '',
  sampleProfiles: [] as { name: string; title: string; company: string }[],
  ctaText: '',
  moreCount: 0
} as const;
export const ACTIVITY_METRICS = {
  line: '',
  showMetrics: false,
  metrics: [] as { value: string; label: string }[]
} as const;

// ============================================================================
// §3 Plays: the deck v5 plays slide (Oct 10). Three groups, one shared
// customer context. `inDevelopment` plays render with a pill and no link.
// ============================================================================

export type LandingPlay = {
  name: string;
  description: string;
  href?: string;
  inDevelopment?: boolean;
};

export const PLAYS_CONTENT = {
  eyebrow: 'The plays',
  headline: 'Lumnis runs the plays that help GTM teams scale.',
  subtitle:
    'AI sales tools automated the cold sequence. Lumnis runs every play from research to the next move, so your reps spend their hours selling.',
  groups: [
    {
      title: 'Build pipeline',
      plays: [
        {
          name: 'Account prioritization',
          description:
            'Watches 1,000+ accounts in your CRM and gives each rep the ones to act on this week, with why now, the evidence and the next move.',
          href: '/continuous-account-prioritization'
        },
        {
          name: 'Top-account outreach',
          description:
            'Picks the five accounts worth a rep’s week, finds the right people and researches what each one cares about right now.'
        },
        {
          name: 'Conference outreach',
          description:
            'Ranks the attendees worth meeting, finds the right person at each company and drafts the invitations.'
        }
      ]
    },
    {
      title: 'Progress accounts',
      plays: [
        {
          name: 'Account progression',
          description:
            'When a new person joins a late-stage deal, Lumnis researches who they are, what they care about and who they listen to, so the rep walks into the call prepared. When the account announces an acquisition or someone on the deal engages a competitor, the rep sees it in the next account update.'
        },
        {
          name: 'Scale your best-seller’s judgment',
          description:
            'Learns how your top reps work a deal from what they approve and change, then brings that judgment to every rep’s accounts.'
        }
      ]
    },
    {
      title: 'Grow audience',
      plays: [
        {
          name: 'Audience building',
          description:
            'Connects with target buyers, writes posts on what they care about and turns the people who engage into conversations.',
          href: '/compounding-audience-engine'
        },
        {
          name: 'Newsletter',
          description:
            'Writes on what your audience cares about and follows up with the readers who engage.',
          inDevelopment: true
        }
      ]
    }
  ] satisfies ReadonlyArray<{ title: string; plays: LandingPlay[] }>,
  contextBar: {
    label: 'Customer context',
    body: 'Your business, buyers, accounts and relationships, shared by every play, so each one makes the next one better.'
  }
} as const;

// ============================================================================
// §3b Competition: the deck v5 landscape slide, same points and positions
// (hq/fundraising/deck/2026-10-09-v5/build.py). x = play coverage, y = how
// much of the work is done for you, both 0 to 1.
// ============================================================================

export type LandscapePoint = {
  name: string;
  label: string;
  x: number;
  y: number;
  anchor?: 'left' | 'right';
  hero?: boolean;
};

export const COMPETITION_CONTENT = {
  eyebrow: 'Competition',
  headlineLead: 'Lumnis covers every GTM play and ',
  headlineHighlight: 'does the work for you',
  yAxis: 'You do the work → Done for you',
  xAxisStart: 'Cold outbound only',
  xAxisEnd: 'Every GTM play',
  points: [
    {
      name: 'Lumnis',
      label: 'every play, done for you',
      x: 0.9,
      y: 0.88,
      anchor: 'right',
      hero: true
    },
    { name: '11x, Artisan', label: 'AI SDRs', x: 0.1, y: 0.74 },
    { name: 'Monaco', label: 'AI plus sales experts', x: 0.3, y: 0.5 },
    {
      name: 'Clay + GTM engineer',
      label: 'a person builds the workflows',
      x: 0.56,
      y: 0.5
    },
    {
      name: 'GTM agencies',
      label: 'people do the work for you',
      x: 0.58,
      y: 0.74
    },
    { name: 'ZoomInfo, Apollo', label: 'data to act on', x: 0.1, y: 0.14 },
    // Added on the site (Abu, Oct 10); not on the deck slide.
    { name: 'Unify', label: 'signals plus sequences', x: 0.16, y: 0.6 },
    {
      name: 'Outreach, Salesloft',
      label: 'sequences you run',
      x: 0.2,
      y: 0.3
    },
    { name: 'Common Room', label: 'signals to act on', x: 0.5, y: 0.22 },
    {
      name: 'AEs and SDRs on their own',
      label: 'every play, on their own time',
      x: 0.88,
      y: 0.14,
      anchor: 'right'
    }
  ] satisfies LandscapePoint[]
} as const;
